import React, { useState, useRef, useEffect, useCallback } from 'react';
import { conciergeService } from '../../../_services/concierge.service';
import { readConciergeSession, saveConciergeSession, clearConciergeSession, conciergeDeviceId } from '../../../_services/conciergeSessionStore';
import useVoiceChat, { voiceSupported } from './useVoiceChat';
// The drawing only (SVG). The 3D Bailey never loads on the booking page.
import { BaileyStill } from '../../agent/bailey/BaileyAvatar';

/**
 * AI Concierge chat widget — floats on the public booking page.
 *
 * Renders NOTHING unless the salon has switched the assistant on. Enablement is
 * checked on mount via the public status endpoint — an earlier version rendered
 * the bubble optimistically and only hid it after a message was rejected, which
 * meant a salon that never opted in showed a chat bubble that greeted the client
 * and then refused. Fails closed (hidden) on any error: the assistant must never
 * break, or clutter, a booking page.
 *
 * Identity: whatever token is in localStorage rides along; logged-in clients
 * get direct booking, anonymous ones get the secure booking-link handoff.
 * Session id and its token persist in sessionStorage (conciergeSessionStore)
 * so a page hop keeps the conversation; a signed-in client's chat is dropped
 * as soon as they're no longer the one signed in, and on logout.
 */

const ink = '#29282D';
const teal = '#007EA0';
const line = '#ECECF0';

/** "Today" / "Tomorrow" / "Friday" — how a person refers to a day, not 2026-08-07. */
// The day as a client says it after a time: "today", "tomorrow", "on Friday",
// "on Wed 7 Oct" (never "on Today").
const whenSaid = (iso) => {
    const label = dayLabel(iso);
    if (label === 'Today' || label === 'Tomorrow') return label.toLowerCase();
    return label ? `on ${label}` : '';
};

const dayLabel = (iso) => {
    if (!iso) return '';
    const d = new Date(`${iso}T12:00:00`);
    if (Number.isNaN(d.getTime())) return iso;
    const today = new Date(); today.setHours(12, 0, 0, 0);
    const diff = Math.round((d - today) / 86400000);
    if (diff === 0) return 'Today';
    if (diff === 1) return 'Tomorrow';
    if (diff > 1 && diff < 7) return d.toLocaleDateString('en-GB', { weekday: 'long' });
    return d.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' });
};

// Injected once rather than added to a stylesheet: this widget is entirely
// self-contained (inline styles throughout) and renders on the public booking
// page, so it shouldn't depend on another file being loaded.
const TYPING_CSS = `
@keyframes beuTypingBounce { 0%,80%,100% { transform: translateY(0); opacity:.4 } 40% { transform: translateY(-4px); opacity:1 } }
.beu-typing-dot { width:6px; height:6px; border-radius:50%; background:#8E8E93; display:inline-block; animation: beuTypingBounce 1.2s infinite ease-in-out; }
.beu-typing-dot--2 { animation-delay:.16s }
.beu-typing-dot--3 { animation-delay:.32s }
@media (prefers-reduced-motion: reduce) { .beu-typing-dot { animation: none; opacity:.6 } }
`;

// `hidden`: the page has a booking step or other sheet open. The bubble would
// sit on that sheet's footer (Choose Slot, Book, totals), so it steps aside,
// staying mounted so an open conversation is still there when it returns.
// It also steps aside by itself whenever the page is scroll-locked, which is
// what every sheet does (useScrollLock pins the body), so sheets opened deep
// inside the page, like a gift card from the wallet cue, are covered too.
// `lifted`: the page's sticky Book Now bar is up, so the bubble rides above
// it, moving with the bar's own slide.
const LIFT = 'translateY(calc(-84px - env(safe-area-inset-bottom, 0px)))';
const reducedMotion = () => {
    try { return window.matchMedia('(prefers-reduced-motion: reduce)').matches; } catch (e) { return false; }
};

const pageLocked = () => document.body.style.position === 'fixed';

/** True while some sheet holds the page's scroll lock. */
const usePageLocked = () => {
    const [locked, setLocked] = useState(pageLocked);
    useEffect(() => {
        if (typeof MutationObserver === 'undefined') return undefined;
        const observer = new MutationObserver(() => setLocked(pageLocked()));
        observer.observe(document.body, { attributes: true, attributeFilter: ['style'] });
        return () => observer.disconnect();
    }, []);
    return locked;
};

// A phone: the open chat is a full-screen sheet, not a floating card.
const PHONE_QUERY = '(max-width: 640px)';
const isPhoneNow = () => {
    try { return window.matchMedia(PHONE_QUERY).matches; } catch (e) { return false; }
};
const useIsPhone = () => {
    const [phone, setPhone] = useState(isPhoneNow);
    useEffect(() => {
        let mq = null;
        try { mq = window.matchMedia(PHONE_QUERY); } catch (e) { return undefined; }
        const on = () => setPhone(mq.matches);
        if (mq.addEventListener) mq.addEventListener('change', on); else if (mq.addListener) mq.addListener(on);
        return () => { if (mq.removeEventListener) mq.removeEventListener('change', on); else if (mq.removeListener) mq.removeListener(on); };
    }, []);
    return phone;
};
// A tapped time is sent as the client would say it: "13:30 today, please",
// "10:00 on Friday, please", "14:00 on Wed 7 Oct, please (2026-10-07)":
// the ISO date is for the assistant, so it books the right day. The client
// only needs the words, in their own bubble and in a reloaded history.
const shownUserText = (text) => String(text || '').replace(/\s*\(\d{4}-\d{2}-\d{2}\)\s*$/, '');

// Bailey is told to write plain text, but a stray **bold** shouldn't show as
// asterisks: render it bold, and a markdown "- " / "* " list line as a bullet.
export const plainReply = (text) => String(text || '')
    .split('\n')
    .map((line) => line.replace(/^\s*[-*]\s+/, '• ').replace(/^#{1,6}\s+/, ''))
    .join('\n')
    .split(/(\*\*[^*\n]+\*\*)/g)
    .map((part, i) => (/^\*\*[^*\n]+\*\*$/.test(part) ? <strong key={i}>{part.slice(2, -2)}</strong> : part));

// The API's cap on one message (config concierge.maxMessageChars). Enforced
// here too so nobody types a page only to be told it's too long; the counter
// shows from COUNTER_FROM on.
export const MAX_MESSAGE_CHARS = 2000;
const COUNTER_FROM = 1800;

// The API refuses with a machine-readable `reason`. Only these reasons' own
// words are shown; anything else (an unknown reason, a database or provider
// error that slipped through, a network failure) gets our fixed copy, so a raw
// provider error never reaches a client.
const GENERIC_ERROR = 'Sorry, I couldn’t answer just now. Please try again in a moment, or contact the salon directly.';
const REFUSAL_COPY = {
    rate_limited: 'Too many messages. Give me a second to catch up.',
    too_long: `That message is a bit long for me. Could you shorten it to under ${MAX_MESSAGE_CHARS.toLocaleString('en-GB')} characters?`,
    // No captcha is shown in the widget yet (no vendor is wired on either
    // side), so asking the client to "confirm you're not a robot" would be a
    // dead end. Point them at the booking page instead.
    captcha_required: 'I can’t start a new chat right now. You can still book on this page, or contact the salon directly.',
    page_unverified: 'The assistant isn’t available on this page right now. You can still book here, or contact the salon directly.',
    turn_limit: 'We have covered a lot here. Start a new chat, or give the salon a ring and they will pick it up.',
    daily_cap: 'The assistant has taken a lot of messages today. Please contact the salon directly.',
    unavailable: 'The assistant is not available right now. Please contact the salon directly.',
    invalid: 'Sorry, I didn’t catch that. Could you try again?',
    error: GENERIC_ERROR
};
// Reasons whose server wording is ours and safe to show as sent (it carries
// detail like "from here for now" that the fixed copy lacks).
const SERVER_WORDS_OK = { rate_limited: 1, too_long: 1, turn_limit: 1, daily_cap: 1, unavailable: 1, error: 1 };

/** What to show for a refusal: the API's words for known reasons, else ours. */
export const refusalText = (e) => {
    const reason = e && e.reason;
    const msg = e && typeof e.responseMessage === 'string' ? e.responseMessage.trim() : '';
    if (reason && SERVER_WORDS_OK[reason] && msg) return msg;
    return (reason && REFUSAL_COPY[reason]) || GENERIC_ERROR;
};

const telHref = (phone) => `tel:${String(phone || '').replace(/[^\d+]/g, '')}`;

const ConciergeWidget = ({ salonId, salonName, acquisitionChannel, proHairdresserId, proSlug, hidden = false, lifted = false }) => {
    // A self-employed stylist's own page (/book/pro/:slug): the API needs the
    // page named and proved (stylist's user id + their slug), and the chat is
    // the stylist's from the first message. It is kept apart from the salon
    // page's chat on this device, so one never continues as the other.
    const onProPage = !!(proHairdresserId && proSlug);
    const storeKey = onProPage ? `${salonId}_pro_${proHairdresserId}` : salonId;
    const sheetOpen = usePageLocked();
    const phone = useIsPhone();
    const [open, setOpen] = useState(false);
    const [enabled, setEnabled] = useState(false);
    // The salon's own first line, when it has written one (Settings → How it talks).
    const [openingLine, setOpeningLine] = useState('');
    // The API's AI + transcript disclosure: always on screen while chatting.
    const [disclosure, setDisclosure] = useState('');
    // Paused by the anomaly brake (status says so): the chat still answers,
    // with the booking link and the salon's number.
    const [assistantPaused, setAssistantPaused] = useState(false);
    const [name, setName] = useState('');
    const [avatar, setAvatar] = useState(null);
    // The salon's team has taken this chat over: Bailey is quiet and a person
    // replies here (Bailey's inbox). pausedBy = who.
    const [paused, setPaused] = useState(false);
    const [pausedBy, setPausedBy] = useState('');
    const [messages, setMessages] = useState([]);
    const [input, setInput] = useState('');
    const [sending, setSending] = useState(false);
    const scrollRef = useRef(null);
    // The session whose conversation is on screen. When storage no longer
    // holds it (a logout, another tab, New chat), what's on screen isn't
    // this person's any more.
    const liveSessionRef = useRef(null);

    // Enablement gate — nothing renders until this says yes.
    useEffect(() => {
        let cancelled = false;
        if (!salonId) return undefined;
        conciergeService.status(salonId)
            .then((res) => {
                if (cancelled) return;
                const data = (res.data && (res.data.responseData || res.data.data)) || res.data;
                if (data && data.enabled) {
                    setEnabled(true);
                    setName(data.assistantName || '');
                    setOpeningLine(data.openingLine || '');
                    setDisclosure(typeof data.disclosure === 'string' ? data.disclosure : '');
                    setAssistantPaused(!!data.paused);
                    setAvatar(data.avatar || null);
                    // Rehydrate a continuing conversation — the BE remembers
                    // the session; the client must see what it remembers.
                    const saved = readConciergeSession(storeKey);
                    if (saved) {
                        liveSessionRef.current = saved.sessionId;
                        conciergeService.history(salonId, saved).then((h) => {
                            if (cancelled) return;
                            const hd = (h.data && (h.data.responseData || h.data.data)) || h.data;
                            if (hd && hd.messages && hd.messages.length) setMessages(hd.messages);
                            if (hd) { setPaused(!!hd.paused); setPausedBy(hd.pausedBy || ''); }
                        }).catch(() => {});
                    }
                }
            })
            .catch(() => { /* fail closed — stay hidden */ });
        return () => { cancelled = true; };
    }, [salonId, storeKey]);

    useEffect(() => {
        if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }, [messages, open]);

    // "New chat" forgets the session AND any takeover state from the old one.
    const newChat = useCallback(() => {
        clearConciergeSession(storeKey);
        liveSessionRef.current = null;
        setMessages([]);
        setPaused(false);
        setPausedBy('');
    }, [storeKey]);

    // The API refuses with HTTP 200 + statusCode 0 and says why (error.reason).
    // Switched off → hide; turn limit → offer a new chat; an error carries the
    // booking link and the salon's number (actions / phone) — never a dead end.
    // Words: see refusalText (never a raw provider error).
    const refused = useCallback((e) => {
        const msg = e && e.responseMessage;
        if ((e && e.reason === 'disabled') || (msg && typeof msg === 'string' && msg.indexOf('not enabled') !== -1)) {
            setEnabled(false);
            setOpen(false);
            return;
        }
        setMessages((m) => [...m, {
            role: 'assistant',
            text: refusalText(e),
            actions: (e && Array.isArray(e.actions)) ? e.actions : [],
            phone: (e && typeof e.phone === 'string' && e.phone) || null,
            newChat: !!(e && e.reason === 'turn_limit')
        }]);
    }, []);

    const send = useCallback(async (text) => {
        const body = (text || '').trim();
        if (!body || sending) return;
        // Over the cap (a pasted wall, or dictation): say so here rather than
        // spend a request the API will refuse. The text stays in the box.
        if (body.length > MAX_MESSAGE_CHARS) {
            setMessages((m) => [...m, { role: 'assistant', text: REFUSAL_COPY.too_long }]);
            return;
        }
        setSending(true);
        const saved = readConciergeSession(storeKey);
        // The chat on screen is gone from storage (someone logged out): start
        // clean rather than carry their conversation into this one.
        const gone = !!liveSessionRef.current && (!saved || saved.sessionId !== liveSessionRef.current);
        if (gone) { liveSessionRef.current = null; setPaused(false); setPausedBy(''); }
        setMessages((m) => [...(gone ? [] : m), { role: 'user', text: body }]);
        setInput('');
        try {
            const res = await conciergeService.chat({
                salonId,
                sessionId: (saved && saved.sessionId) || undefined,
                sessionToken: (saved && saved.sessionToken) || undefined,
                message: body,
                // 'salon_profile' | 'pro_link' — which page this chat is on.
                acquisitionChannel,
                // The pro page, named and proved (the API checks the stylist
                // works at this salon and the slug is theirs).
                ...(onProPage ? { proHairdresserId: String(proHairdresserId), proSlug: String(proSlug) } : {}),
                // Per-browser limits only; see conciergeDeviceId.
                deviceId: conciergeDeviceId()
            });
            if (res.data && res.data.statusCode === 0) throw Object.assign(new Error('refused'), { response: res });
            const data = (res.data && (res.data.responseData || res.data.data)) || res.data;
            if (data && data.sessionId) {
                const prev = saved && saved.sessionId;
                saveConciergeSession(storeKey, { sessionId: data.sessionId, sessionToken: data.sessionToken });
                liveSessionRef.current = data.sessionId;
                // BE started a fresh session (the old one went stale, or the
                // signed-in person changed): the old rendered turns belong to
                // a dead thread, and may be someone else's. Keep only what
                // was just sent.
                if (prev && prev !== data.sessionId) {
                    setMessages((m) => m.slice(-1));
                }
            }
            // Two different "paused"s: the salon's team has taken THIS chat
            // over (paused, no assistantPaused), or the receptionist itself is
            // paused — the anomaly brake (assistantPaused) or Beu's cost
            // ceiling (costCeiling). Those two answer with the booking link
            // and the salon's number, and are not a person replying.
            const fallback = !!(data && (data.assistantPaused || data.costCeiling));
            if (data && data.assistantPaused) setAssistantPaused(true);
            if (data && data.paused && !fallback) {
                // A person has this chat: no Bailey reply. Say so once.
                setPaused(true);
                setPausedBy(data.pausedBy || '');
                if (!paused && data.note) setMessages((m) => [...m, { role: 'note', text: data.note }]);
            } else {
                setPaused(false);
                setMessages((m) => [...m, {
                    role: 'assistant',
                    text: (data && data.reply) || 'Sorry, I had trouble with that.',
                    actions: (data && Array.isArray(data.actions)) ? data.actions : [],
                    phone: (data && typeof data.phone === 'string' && data.phone) || null
                }]);
            }
        } catch (err) {
            refused(err && err.response && err.response.data && err.response.data.error);
        }
        setSending(false);
    }, [salonId, storeKey, onProPage, proHairdresserId, proSlug, sending, paused, acquisitionChannel, refused]);

    // While the chat is open, look for replies from the salon's team (a person
    // stepping in from Bailey's inbox). The server's transcript wins, so a
    // reply lands in order; not while a message of ours is on its way.
    useEffect(() => {
        if (!open || !enabled || !salonId) return undefined;
        const tick = () => {
            if (sending || document.hidden) return;
            const saved = readConciergeSession(storeKey);
            if (!saved) {
                // Signed out (here or in another tab) with their chat open.
                if (liveSessionRef.current) {
                    liveSessionRef.current = null;
                    setMessages([]); setPaused(false); setPausedBy('');
                }
                return;
            }
            conciergeService.history(salonId, saved).then((h) => {
                const hd = (h.data && (h.data.responseData || h.data.data)) || h.data;
                if (!hd || !Array.isArray(hd.messages)) return;
                setPaused(!!hd.paused);
                setPausedBy(hd.pausedBy || '');
                setMessages((cur) => {
                    const theirs = cur.filter((m) => m.role !== 'note');
                    const staffNew = hd.messages.filter((m) => m.from === 'staff').length > theirs.filter((m) => m.from === 'staff').length;
                    if (!staffNew && hd.messages.length <= theirs.length) return cur;
                    // Keep the booking buttons on Bailey's latest reply.
                    const last = theirs[theirs.length - 1];
                    const merged = hd.messages.slice();
                    const tail = merged[merged.length - 1];
                    if (last && tail && last.role === 'assistant' && tail.role === 'assistant' && last.text === tail.text && last.actions) merged[merged.length - 1] = { ...tail, actions: last.actions };
                    return merged;
                });
            }).catch(() => {});
        };
        const t = setInterval(tick, 5000);
        return () => clearInterval(t);
    }, [open, enabled, salonId, storeKey, sending]);

    // Hands-free mode. Drives the SAME send() as typing, so voice can't diverge
    // from text — one code path, one set of guardrails.
    const voice = useVoiceChat({ onUtterance: send, busy: sending });

    // Speak each new assistant reply while hands-free is on. Keyed on the last
    // message so a re-render can't make it read the same line twice.
    const spokenRef = useRef(null);
    useEffect(() => {
        if (!voice.active || !messages.length) return;
        const last = messages[messages.length - 1];
        if (last.role !== 'assistant') return;
        const key = `${messages.length}:${last.text.slice(0, 40)}`;
        if (spokenRef.current === key) return;
        spokenRef.current = key;
        voice.speak(last.text);
    }, [messages, voice]);

    const fullScreen = open && phone;
    useEffect(() => {
        if (!fullScreen) return undefined;
        const html = document.documentElement;
        const before = html.style.overflow;
        html.style.overflow = 'hidden';
        return () => { html.style.overflow = before; };
    }, [fullScreen]);

    if (!salonId || !enabled) return null;

    return (
        <div style={Object.assign({
            position: 'fixed', right: 20, bottom: 20, zIndex: 100030, fontFamily: 'inherit',
            // The page's own text rules (nowrap on some of its blocks) must not
            // reach the chat: the greeting ran off the edge on phones.
            whiteSpace: 'normal', textAlign: 'left',
            display: hidden || sheetOpen ? 'none' : undefined,
            transform: lifted ? LIFT : 'none',
            transition: reducedMotion() ? 'none' : 'transform 0.3s ease'
        }, fullScreen ? { top: 0, left: 0, right: 0, bottom: 0, transform: 'none' } : null)}>
            <style>{'.cw-face svg{width:100%;height:100%;display:block}'}</style>
            {open && (
                <div
                    role="dialog"
                    aria-label={`Chat with ${name || 'Bailey'}`}
                    style={fullScreen ? {
                        // Phone: the whole screen, like a messaging app.
                        width: '100%', height: '100%', background: '#fff',
                        display: 'flex', flexDirection: 'column', overflow: 'hidden'
                    } : {
                        width: 340, height: 460, maxHeight: 'calc(100vh - 110px)', marginBottom: 12, background: '#fff', borderRadius: 16,
                        boxShadow: '0 12px 40px rgba(0,0,0,0.18)', display: 'flex', flexDirection: 'column', overflow: 'hidden'
                    }}>
                    <style>{TYPING_CSS}</style>
                    <div style={{
                        padding: '14px 16px', background: ink, color: '#fff', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8,
                        paddingTop: fullScreen ? 'calc(12px + env(safe-area-inset-top, 0px))' : 14
                    }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
                            <span className="cw-face" style={{ width: 34, height: 34, borderRadius: '50%', background: '#F1EFEA', overflow: 'hidden', flexShrink: 0, display: 'flex', alignItems: 'flex-end', justifyContent: 'center' }} aria-hidden="true">
                                <BaileyStill appearance={avatar || undefined} face />
                            </span>
                            <div style={{ minWidth: 0 }}>
                                <div style={{ fontWeight: 600, fontSize: 15 }}>{name || 'Bailey'}</div>
                                <div style={{ fontSize: 12, opacity: 0.75, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                    {paused
                                        ? `${pausedBy ? pausedBy.split(' ')[0] : 'The team'} from ${salonName || 'the salon'} is replying`
                                        : name && name !== 'Bailey' ? 'AI receptionist, powered by Bailey' : `AI receptionist at ${salonName || 'the salon'}`}
                                </div>
                            </div>
                        </div>
                        <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexShrink: 0 }}>
                        {voiceSupported() && (
                            <button type="button"
                                title={voice.active ? 'Stop talking' : 'Talk instead of typing'}
                                onClick={() => (voice.active ? voice.stop() : voice.start())}
                                style={{
                                    background: voice.active ? '#E5484D' : 'rgba(255,255,255,0.14)',
                                    border: 'none', color: '#fff', borderRadius: 8, padding: '6px 10px',
                                    fontSize: 12, cursor: 'pointer', flexShrink: 0
                                }}>
                                {voice.active ? 'End call' : 'Call'}
                            </button>
                        )}
                        {messages.length > 0 && (
                            <button type="button" title="Start a new conversation"
                                onClick={newChat}
                                style={{ background: 'rgba(255,255,255,0.14)', border: 'none', color: '#fff', borderRadius: 8, padding: '6px 10px', fontSize: 12, cursor: 'pointer', flexShrink: 0 }}>
                                New chat
                            </button>
                        )}
                        {fullScreen && (
                            // Phone: the launcher is under the sheet, so the sheet closes itself.
                            <button type="button" aria-label="Close the chat" onClick={() => setOpen(false)}
                                style={{ background: 'rgba(255,255,255,0.14)', border: 'none', color: '#fff', borderRadius: 8, width: 32, height: 30, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', flexShrink: 0 }}>
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" aria-hidden="true"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
                            </button>
                        )}
                        </div>
                    </div>
                    {(voice.active || voice.error) && (
                        <div style={{
                            padding: '8px 14px', fontSize: 12, background: voice.error ? '#FFF4F4' : '#F2FAFC',
                            color: voice.error ? '#B42318' : teal, borderBottom: `1px solid ${line}`
                        }}>
                            {voice.error
                                ? voice.error
                                : voice.speaking ? 'Speaking…'
                                    : sending ? 'Thinking…'
                                        : voice.listening ? (voice.partial || 'Listening… just start talking')
                                            : 'Starting the microphone…'}
                        </div>
                    )}
                    {/* AI + transcript disclosure, from the API, above the chat at
                        all times (hardening 2026-10). Not dismissible. Carries
                        the privacy link (audit 2026-09-29 P1-10). */}
                    <div data-testid="concierge-disclosure" style={{
                        padding: '6px 12px', fontSize: 11.5, lineHeight: 1.4, color: '#5F5F66',
                        background: '#fff', borderBottom: `1px solid ${line}`
                    }}>
                        {disclosure || `You’re chatting with ${name || 'Bailey'}, an AI assistant. Chats are stored and may be read by the salon team.`}{' '}
                        <a href="/privacy-policy" target="_blank" rel="noopener noreferrer"
                            style={{ color: teal, textDecoration: 'underline' }}>Privacy</a>
                    </div>
                    {assistantPaused && (
                        <div role="status" style={{
                            padding: '7px 12px', fontSize: 12, lineHeight: 1.4, color: '#7A4B00',
                            background: '#FFF8EB', borderBottom: `1px solid ${line}`
                        }}>
                            {name || 'Bailey'} isn’t answering at the moment. You can still book on this page{salonName ? `, or contact ${salonName} directly` : ''}.
                        </div>
                    )}
                    <div ref={scrollRef} style={{ flex: 1, overflowY: 'auto', padding: 14, background: '#FAFAFB' }}>
                        {messages.length === 0 && (
                            <div style={{ color: '#8E8E93', fontSize: 13, lineHeight: 1.5 }}>
                                {openingLine || `Hi, I’m ${name || 'Bailey'}! I can check prices, find you an appointment, or pass a message to the team. What do you need?`}
                            </div>
                        )}
                        {messages.map((m, i) => (m.role === 'note' ? (
                            <div key={i} style={{ margin: '4px 0 10px', textAlign: 'center', fontSize: 12, lineHeight: 1.45, color: '#8E8E93' }}>{m.text}</div>
                        ) : (
                            <div key={i} style={{ marginBottom: 8 }}>
                                {m.from === 'staff' && (
                                    <div style={{ fontSize: 11.5, fontWeight: 600, color: '#6E6E76', margin: '0 0 3px 2px' }}>
                                        {(m.name || 'The team').split(' ')[0]} from {salonName || 'the salon'}
                                    </div>
                                )}
                                <div style={{ display: 'flex', justifyContent: m.role === 'user' ? 'flex-end' : 'flex-start' }}>
                                    <div style={{
                                        maxWidth: '82%', padding: '9px 12px', borderRadius: 12, fontSize: 14, lineHeight: 1.45,
                                        whiteSpace: 'pre-wrap', wordBreak: 'break-word',
                                        background: m.role === 'user' ? teal : '#fff',
                                        color: m.role === 'user' ? '#fff' : ink,
                                        border: m.role === 'user' ? 'none' : `1px solid ${line}`
                                    }}>{m.role === 'user' ? shownUserText(m.text) : plainReply(m.text)}</div>
                                </div>
                                {m.newChat && i === messages.length - 1 && (
                                    <button type="button" onClick={newChat}
                                        style={{ marginTop: 8, background: '#fff', border: `1px solid ${teal}`, color: teal, borderRadius: 8, padding: '7px 12px', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
                                        Start a new chat
                                    </button>
                                )}
                                {/* CTAs — only on the LATEST assistant message so stale
                                    buttons (old slots, superseded links) don't linger. */}
                                {m.role === 'assistant' && i === messages.length - 1 && (m.actions || []).map((a, j) => {
                                    if (a.type === 'slots') {
                                        // Grouped BY DAY. A bare row of times was
                                        // unanswerable — "07:25" for which day? —
                                        // and when the reply described three days
                                        // the chips silently showed one.
                                        const days = a.days && a.days.length
                                            ? a.days
                                            : [{ date: a.date, slots: a.slots || [] }];
                                        return (
                                            <div key={j} style={{ marginTop: 10 }}>
                                                {days.map((d) => (
                                                    <div key={d.date} style={{ marginBottom: 10 }}>
                                                        <div style={{
                                                            fontSize: 11, fontWeight: 700, letterSpacing: '.04em',
                                                            textTransform: 'uppercase', color: '#8E8E93', marginBottom: 6
                                                        }}>{dayLabel(d.date)}</div>
                                                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                                                            {(d.slots || []).map((slot) => (
                                                                <button key={`${d.date}-${slot}`} type="button" disabled={sending}
                                                                    onClick={() => send(`${slot} ${whenSaid(d.date)}, please (${d.date})`)}
                                                                    style={{
                                                                        border: `1px solid ${teal}`, background: '#fff', color: teal,
                                                                        borderRadius: 16, padding: '7px 13px', fontSize: 13, fontWeight: 600,
                                                                        cursor: sending ? 'default' : 'pointer', opacity: sending ? 0.5 : 1
                                                                    }}>{slot}</button>
                                                            ))}
                                                        </div>
                                                    </div>
                                                ))}
                                                {/* An escape hatch beats a wall of chips: most people
                                                    have a rough time in mind and would rather say it. */}
                                                <button type="button" disabled={sending}
                                                    onClick={() => send('None of those work. What else is there?')}
                                                    style={{
                                                        border: 'none', background: 'none', color: '#8E8E93',
                                                        fontSize: 12, textDecoration: 'underline', cursor: 'pointer', padding: 0
                                                    }}>None of these suit</button>
                                            </div>
                                        );
                                    }
                                    if (a.type === 'confirm') {
                                        // The assistant has read a specific booking
                                        // back. The answer to "shall I pop you in?"
                                        // is yes — so one primary action, not a row
                                        // of times to re-pick from.
                                        const p = a.proposal || {};
                                        return (
                                            <div key={j} style={{ marginTop: 10 }}>
                                                <div style={{
                                                    border: `1px solid ${line}`, borderRadius: 12, padding: '10px 12px',
                                                    background: '#fff', marginBottom: 8, fontSize: 13, lineHeight: 1.5, color: ink
                                                }}>
                                                    <div style={{ fontWeight: 700 }}>{(p.services || []).join(' + ')}</div>
                                                    <div style={{ color: '#6E6E73' }}>
                                                        {p.stylist ? `${p.stylist} · ` : ''}{dayLabel(p.date)}{p.slot ? ` at ${p.slot}` : ''}
                                                    </div>
                                                    {typeof p.totalPrice === 'number' && p.totalPrice > 0 && (
                                                        <div style={{ fontWeight: 700, marginTop: 2 }}>£{p.totalPrice}</div>
                                                    )}
                                                </div>
                                                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                                                    <button type="button" disabled={sending}
                                                        onClick={() => send('Yes please, book it.')}
                                                        style={{
                                                            background: teal, color: '#fff', border: 'none', borderRadius: 10,
                                                            padding: '10px 18px', fontSize: 14, fontWeight: 700,
                                                            cursor: sending ? 'default' : 'pointer', opacity: sending ? 0.6 : 1
                                                        }}>Confirm booking</button>
                                                    <button type="button" disabled={sending}
                                                        onClick={() => send('Actually, can I pick a different time?')}
                                                        style={{
                                                            background: 'none', border: 'none', color: '#8E8E93',
                                                            fontSize: 12, textDecoration: 'underline', cursor: 'pointer', padding: 0
                                                        }}>Change</button>
                                                </div>
                                            </div>
                                        );
                                    }
                                    if (a.type === 'link') {
                                        return (
                                            <div key={j} style={{ marginTop: 8 }}>
                                                <a href={a.url} style={{
                                                    display: 'inline-block', background: teal, color: '#fff', textDecoration: 'none',
                                                    borderRadius: 10, padding: '10px 16px', fontSize: 14, fontWeight: 600
                                                }}>{a.label || 'Continue'}</a>
                                            </div>
                                        );
                                    }
                                    if (a.type === 'booked') {
                                        return (
                                            <div key={j} style={{
                                                marginTop: 8, display: 'inline-block', background: 'rgba(0,126,160,0.08)',
                                                border: `1px solid ${teal}`, color: teal, borderRadius: 10,
                                                padding: '8px 14px', fontSize: 13, fontWeight: 600
                                            }}>✓ Booked{a.date ? ` for ${a.date}${a.slot ? ` at ${a.slot}` : ''}` : ''}</div>
                                        );
                                    }
                                    return null;
                                })}
                                {/* The salon's own number, when the API sent one with a
                                    fallback (paused, cost ceiling, an error). */}
                                {m.role === 'assistant' && i === messages.length - 1 && m.phone && (
                                    <div style={{ marginTop: 8 }}>
                                        <a href={telHref(m.phone)} style={{
                                            display: 'inline-block', background: '#fff', color: teal, textDecoration: 'none',
                                            border: `1px solid ${teal}`, borderRadius: 10, padding: '9px 15px', fontSize: 14, fontWeight: 600
                                        }}>Call {m.phone}</a>
                                    </div>
                                )}
                            </div>
                        )))}
                        {/* Typing indicator — a bare "…" gave no sense that anything
                            was happening. Named, animated, and in a bubble on the
                            assistant's side, so a two-second tool call reads as
                            someone checking rather than a frozen page. */}
                        {sending && (
                            <div style={{ display: 'flex', justifyContent: 'flex-start', marginBottom: 8 }}>
                                <div style={{
                                    background: '#fff', border: `1px solid ${line}`, borderRadius: 12,
                                    padding: '10px 13px', display: 'flex', gap: 5, alignItems: 'center'
                                }}>
                                    <span className="beu-typing-dot" />
                                    <span className="beu-typing-dot beu-typing-dot--2" />
                                    <span className="beu-typing-dot beu-typing-dot--3" />
                                    <span style={{ fontSize: 12, color: '#8E8E93', marginLeft: 4 }}>
                                        {(name || 'Assistant')} is typing
                                    </span>
                                </div>
                            </div>
                        )}
                    </div>
                    {input.length >= COUNTER_FROM && (
                        <div aria-live="polite" style={{
                            padding: '4px 12px 0', fontSize: 11.5, textAlign: 'right', background: '#fff',
                            borderTop: `1px solid ${line}`,
                            color: input.length >= MAX_MESSAGE_CHARS ? '#B42318' : '#8E8E93'
                        }}>
                            {input.length.toLocaleString('en-GB')} / {MAX_MESSAGE_CHARS.toLocaleString('en-GB')}
                        </div>
                    )}
                    <div style={{
                        display: 'flex', gap: 8, padding: 10, borderTop: input.length >= COUNTER_FROM ? 'none' : `1px solid ${line}`, background: '#fff',
                        paddingBottom: fullScreen ? 'calc(10px + env(safe-area-inset-bottom, 0px))' : 10
                    }}>
                        <input
                            value={input}
                            maxLength={MAX_MESSAGE_CHARS}
                            aria-label="Message"
                            onChange={(e) => setInput(e.target.value.slice(0, MAX_MESSAGE_CHARS))}
                            onKeyDown={(e) => { if (e.key === 'Enter') send(input); }}
                            placeholder="Type a message…"
                            // 16px on phones: anything smaller and iOS zooms the page in on focus.
                            style={{ flex: 1, minWidth: 0, border: `1px solid ${line}`, borderRadius: 10, padding: '9px 12px', fontSize: phone ? 16 : 14, outline: 'none' }}
                        />
                        <button
                            type="button"
                            onClick={() => send(input)}
                            disabled={sending || !input.trim()}
                            style={{
                                border: 'none', borderRadius: 10, padding: '9px 14px', fontWeight: 600, fontSize: 14,
                                background: ink, color: '#fff', cursor: sending ? 'default' : 'pointer',
                                opacity: sending || !input.trim() ? 0.5 : 1
                            }}
                        >Send</button>
                    </div>
                </div>
            )}
            {!fullScreen && <button
                type="button"
                aria-label={open ? 'Close the chat' : `Chat with ${name || 'Bailey'}`}
                onClick={() => setOpen(!open)}
                style={{
                    // Frosted glass, so the launcher reads on a dark hero photo
                    // and on white page content without ever going black on black.
                    width: 56, height: 56, borderRadius: '50%', cursor: 'pointer',
                    background: 'rgba(255,255,255,0.72)',
                    WebkitBackdropFilter: 'blur(14px) saturate(140%)',
                    backdropFilter: 'blur(14px) saturate(140%)',
                    border: '1px solid rgba(255,255,255,0.75)',
                    color: ink,
                    boxShadow: '0 8px 24px rgba(0,0,0,0.16), inset 0 1px 0 rgba(255,255,255,0.6)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', marginLeft: 'auto'
                }}
            >
                {open ? (
                    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
                ) : (
                    // Bailey's face, so clients see who they'll be talking to.
                    <span className="cw-face" style={{ width: 46, height: 46, borderRadius: '50%', overflow: 'hidden', background: '#F1EFEA', display: 'flex', alignItems: 'flex-end', justifyContent: 'center' }} aria-hidden="true">
                        <BaileyStill appearance={avatar || undefined} face />
                    </span>
                )}
            </button>}
        </div>
    );
};

export default ConciergeWidget;
